import { useMemo } from "react";
import type { MagazineTemplate, MagazineTitle, MagazineIssue, CoverTemplateConfig, TemplateStyleConfig, ElementBasedTemplateConfig } from "@shared/schema";
import { ElementBasedCoverRenderer } from "./ElementBasedCoverRenderer";

interface CoverPreviewProps {
  template: MagazineTemplate | null;
  title: MagazineTitle;
  issue: MagazineIssue;
  coverImage?: string | null;
  mainArticle?: {
    title: string;
    subtitle?: string | null;
    author?: string | null;
  } | null;
  secondaryArticles?: Array<{
    title: string;
    pageNumber?: number;
  }>;
  className?: string;
}

function isElementBasedConfig(config: unknown): config is ElementBasedTemplateConfig {
  return typeof config === "object" && config !== null && "version" in config && (config as any).version === 2;
}

const defaultLayoutConfig: CoverTemplateConfig = {
  masthead: {
    position: "top",
    showLogo: true,
    showTagline: true,
    height: 12,
  },
  issueInfo: {
    position: "top-right",
    showDate: true,
    showIssueNumber: true,
    showVolume: false,
    showWebsite: false,
  },
  heroImage: {
    style: "full-bleed",
    overlayGradient: true,
    overlayDirection: "bottom",
  },
  mainHeadline: {
    position: "bottom-left",
    showSubtitle: true,
    showByline: false,
    maxLines: 3,
  },
  secondaryHeadlines: {
    enabled: true,
    maxCount: 3,
    positions: [],
  },
  footerBar: {
    enabled: false,
    style: "solid",
    showAlsoInThisIssue: false,
  },
};

const defaultStyleConfig: TemplateStyleConfig = {
  primaryFont: "Inter",
  secondaryFont: "Plus Jakarta Sans",
  headlineFont: "Plus Jakarta Sans",
  primaryColor: "#ffffff",
  secondaryColor: "#f0f0f0",
  accentColor: "#dc2626",
  backgroundColor: "#1a1a1a",
  textColor: "#ffffff",
  overlayOpacity: 0.6,
};

export function CoverPreview({
  template,
  title,
  issue,
  coverImage,
  mainArticle,
  secondaryArticles = [],
  className = "",
}: CoverPreviewProps) {
  // Check if template uses the new element-based system (version 2)
  if (template?.layoutConfig && isElementBasedConfig(template.layoutConfig)) {
    return (
      <ElementBasedCoverRenderer
        config={template.layoutConfig}
        title={title}
        issue={issue}
        coverImage={coverImage}
        mainArticle={mainArticle}
        secondaryArticles={secondaryArticles}
        className={className}
      />
    );
  }

  const layoutConfig = useMemo((): CoverTemplateConfig => {
    const templateLayout = template?.layoutConfig as CoverTemplateConfig | undefined;
    // Deep merge template config with defaults to ensure all properties exist
    return {
      masthead: { ...defaultLayoutConfig.masthead, ...templateLayout?.masthead },
      issueInfo: { ...defaultLayoutConfig.issueInfo, ...templateLayout?.issueInfo },
      heroImage: { ...defaultLayoutConfig.heroImage, ...templateLayout?.heroImage },
      mainHeadline: { ...defaultLayoutConfig.mainHeadline, ...templateLayout?.mainHeadline },
      secondaryHeadlines: { ...defaultLayoutConfig.secondaryHeadlines, ...templateLayout?.secondaryHeadlines },
      footerBar: { ...defaultLayoutConfig.footerBar, ...templateLayout?.footerBar },
    };
  }, [template]);

  const styleConfig = useMemo(() => {
    return (template?.styleConfig as TemplateStyleConfig) || defaultStyleConfig;
  }, [template]);

  const mastheadPositionClass = useMemo(() => {
    switch (layoutConfig.masthead.position) {
      case "top-left": return "justify-start";
      case "top-right": return "justify-end";
      case "top-center": return "justify-center";
      default: return "justify-center";
    }
  }, [layoutConfig.masthead.position]);

  const headlinePositionClass = useMemo(() => {
    switch (layoutConfig.mainHeadline.position) {
      case "bottom-left": return "items-start text-left";
      case "bottom-right": return "items-end text-right";
      case "bottom-center": return "items-center text-center";
      case "center": return "items-center text-center justify-center";
      default: return "items-start text-left";
    }
  }, [layoutConfig.mainHeadline.position]);

  const gradientClass = useMemo(() => {
    if (!layoutConfig.heroImage.overlayGradient) return "";
    switch (layoutConfig.heroImage.overlayDirection) {
      case "bottom": return "bg-gradient-to-t from-black/70 via-black/30 to-transparent";
      case "top": return "bg-gradient-to-b from-black/70 via-black/30 to-transparent";
      case "left": return "bg-gradient-to-r from-black/70 via-black/30 to-transparent";
      case "right": return "bg-gradient-to-l from-black/70 via-black/30 to-transparent";
      default: return "bg-gradient-to-t from-black/70 via-black/30 to-transparent";
    }
  }, [layoutConfig.heroImage]);

  const hasSecondaryPositions = (layoutConfig.secondaryHeadlines.positions?.length ?? 0) > 0;
  const firstPosition = layoutConfig.secondaryHeadlines.positions?.[0]?.position;

  return (
    <div 
      className={`relative overflow-hidden ${className}`}
      style={{ 
        backgroundColor: styleConfig.backgroundColor,
        fontFamily: styleConfig.primaryFont,
      }}
      data-testid="cover-preview"
    >
      {/* Hero Image - Full Bleed */}
      {coverImage && (
        <div className="absolute inset-0">
          <img 
            src={coverImage} 
            alt="Cover" 
            className={`w-full h-full ${
              layoutConfig.heroImage.style === "full-bleed" ? "object-cover" :
              layoutConfig.heroImage.style === "portrait-focus" ? "object-cover object-top" :
              "object-contain"
            }`}
          />
          {/* Overlay gradient */}
          {layoutConfig.heroImage.overlayGradient && (
            <div 
              className={`absolute inset-0 ${gradientClass}`}
              style={{ opacity: styleConfig.overlayOpacity }}
            />
          )}
        </div>
      )}

      {/* Content Container */}
      <div className="relative z-10 flex flex-col h-full p-4">
        {/* Masthead Area */}
        <div 
          className={`flex ${mastheadPositionClass} items-start`}
          style={{ minHeight: `${layoutConfig.masthead.height}%` }}
        >
          <div className="flex flex-col items-center">
            {layoutConfig.masthead.showLogo && (
              <div 
                className="font-bold text-xl tracking-tight"
                style={{ 
                  color: styleConfig.primaryColor,
                  fontFamily: styleConfig.headlineFont,
                }}
              >
                {title.name}
              </div>
            )}
            {layoutConfig.masthead.showTagline && title.tagline && (
              <div 
                className="text-xs opacity-80"
                style={{ color: styleConfig.secondaryColor }}
              >
                {title.tagline}
              </div>
            )}
          </div>

          {/* Issue Info */}
          {layoutConfig.issueInfo.position === "top-right" && (
            <div 
              className="absolute top-4 right-4 text-right text-xs"
              style={{ color: styleConfig.secondaryColor }}
            >
              {layoutConfig.issueInfo.showIssueNumber && (
                <div>Issue {issue.issueNumber}</div>
              )}
              {layoutConfig.issueInfo.showDate && (issue.publicationDate || issue.issueDate) && (
                <div>{new Date((issue.publicationDate || issue.issueDate)!).toLocaleDateString("en-US", { month: "short", year: "numeric" })}</div>
              )}
            </div>
          )}
          {layoutConfig.issueInfo.position === "top-left" && (
            <div 
              className="absolute top-4 left-4 text-left text-xs"
              style={{ color: styleConfig.secondaryColor }}
            >
              {layoutConfig.issueInfo.showIssueNumber && (
                <div>Issue {issue.issueNumber}</div>
              )}
              {layoutConfig.issueInfo.showDate && (issue.publicationDate || issue.issueDate) && (
                <div>{new Date((issue.publicationDate || issue.issueDate)!).toLocaleDateString("en-US", { month: "short", year: "numeric" })}</div>
              )}
            </div>
          )}
          {layoutConfig.issueInfo.position === "below-masthead" && (
            <div 
              className="text-center text-xs mt-1"
              style={{ color: styleConfig.secondaryColor }}
            >
              {layoutConfig.issueInfo.showIssueNumber && (
                <span>Issue {issue.issueNumber}</span>
              )}
              {layoutConfig.issueInfo.showDate && (issue.publicationDate || issue.issueDate) && (
                <span className="ml-2">{new Date((issue.publicationDate || issue.issueDate)!).toLocaleDateString("en-US", { month: "short", year: "numeric" })}</span>
              )}
            </div>
          )}
        </div>

        {/* Main Content Area - grows to fill space */}
        <div className="flex-1 flex flex-col justify-end">
          {/* Secondary Headlines - Render based on positions array */}
          {layoutConfig.secondaryHeadlines.enabled && 
           hasSecondaryPositions && 
           secondaryArticles.length > 0 && (
            <>
              {layoutConfig.secondaryHeadlines.positions.map((posConfig, posIndex) => {
                const article = secondaryArticles[posIndex];
                if (!article) return null;
                
                const positionClasses = {
                  "top-left": "absolute top-12 left-4",
                  "top-right": "absolute top-12 right-4 text-right",
                  "left": "absolute left-4 top-1/3",
                  "right": "absolute right-4 top-1/3 text-right",
                  "bottom-left": "absolute bottom-12 left-4",
                  "bottom-right": "absolute bottom-12 right-4 text-right",
                }[posConfig.position] || "absolute right-4 top-1/3";
                
                return (
                  <div
                    key={posConfig.id || posIndex}
                    className={positionClasses}
                    style={{ 
                      color: styleConfig.secondaryColor,
                      maxWidth: `${posConfig.width}%`,
                    }}
                  >
                    <div className="text-sm font-medium leading-tight">{article.title}</div>
                    {article.pageNumber && (
                      <div className="text-xs opacity-70">p.{article.pageNumber}</div>
                    )}
                  </div>
                );
              })}
            </>
          )}

          {/* Main Headline */}
          <div className={`flex flex-col ${headlinePositionClass}`}>
            {mainArticle && (
              <>
                <h1 
                  className="font-bold text-2xl leading-tight mb-1"
                  style={{ 
                    color: styleConfig.primaryColor,
                    fontFamily: styleConfig.headlineFont,
                    WebkitLineClamp: layoutConfig.mainHeadline.maxLines,
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {mainArticle.title}
                </h1>
                {layoutConfig.mainHeadline.showSubtitle && mainArticle.subtitle && (
                  <p 
                    className="text-sm opacity-90 mb-1"
                    style={{ color: styleConfig.secondaryColor }}
                  >
                    {mainArticle.subtitle}
                  </p>
                )}
                {layoutConfig.mainHeadline.showByline && mainArticle.author && (
                  <p 
                    className="text-xs opacity-70"
                    style={{ color: styleConfig.secondaryColor }}
                  >
                    By {mainArticle.author}
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        {/* Footer Bar */}
        {layoutConfig.footerBar.enabled && (
          <div 
            className={`flex items-center justify-between pt-2 mt-2 ${
              layoutConfig.footerBar.style === "gradient" 
                ? "bg-gradient-to-r from-black/50 to-transparent" 
                : layoutConfig.footerBar.style === "solid" 
                  ? "bg-black/30" 
                  : ""
            } rounded px-2 py-1`}
            style={{ color: styleConfig.secondaryColor }}
          >
            {layoutConfig.footerBar.showAlsoInThisIssue && (
              <span className="text-xs">Also in this issue...</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function CoverPreviewMini({
  template,
  title,
  className = "",
}: {
  template: MagazineTemplate | null;
  title: MagazineTitle;
  className?: string;
}) {
  const styleConfig = useMemo(() => {
    return (template?.styleConfig as TemplateStyleConfig) || defaultStyleConfig;
  }, [template]);

  return (
    <div 
      className={`relative overflow-hidden flex flex-col items-center justify-center ${className}`}
      style={{ 
        backgroundColor: styleConfig.backgroundColor,
        fontFamily: styleConfig.primaryFont,
      }}
      data-testid="cover-preview-mini"
    >
      <div 
        className="font-bold text-lg text-center"
        style={{ 
          color: styleConfig.primaryColor,
          fontFamily: styleConfig.headlineFont,
        }}
      >
        {title.name}
      </div>
      {title.tagline && (
        <div 
          className="text-xs text-center opacity-70"
          style={{ color: styleConfig.secondaryColor }}
        >
          {title.tagline}
        </div>
      )}
      <div 
        className="absolute bottom-2 left-0 right-0 text-center text-xs"
        style={{ color: styleConfig.accentColor }}
      >
        {template?.name || "No Template"}
      </div>
    </div>
  );
}

interface ContentsPreviewProps {
  template: MagazineTemplate | null;
  title: MagazineTitle;
  articles?: Array<{
    title: string;
    section?: string;
    pageNumber?: number;
  }>;
  className?: string;
}

export function ContentsPreview({
  template,
  title,
  articles = [],
  className = "",
}: ContentsPreviewProps) {
  const styleConfig = useMemo(() => {
    return (template?.styleConfig as TemplateStyleConfig) || defaultStyleConfig;
  }, [template]);

  return (
    <div 
      className={`relative overflow-hidden p-6 ${className}`}
      style={{ 
        backgroundColor: styleConfig.backgroundColor || "#ffffff",
        fontFamily: styleConfig.primaryFont,
        color: styleConfig.textColor || "#000000",
      }}
      data-testid="contents-preview"
    >
      <h2 
        className="text-2xl font-bold mb-6 text-center border-b pb-4"
        style={{ 
          fontFamily: styleConfig.headlineFont,
          borderColor: styleConfig.accentColor,
        }}
      >
        Contents
      </h2>
      
      <div className="space-y-4">
        {articles.length > 0 ? (
          articles.map((article, index) => (
            <div 
              key={index}
              className="flex items-baseline justify-between gap-2"
            >
              <div className="flex-1">
                <div className="font-medium">{article.title}</div>
                {article.section && (
                  <div className="text-xs opacity-70">{article.section}</div>
                )}
              </div>
              {article.pageNumber && (
                <div 
                  className="font-bold"
                  style={{ color: styleConfig.accentColor }}
                >
                  {article.pageNumber}
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="text-center py-8 opacity-50">
            <p>Contents will be auto-generated</p>
            <p className="text-sm mt-2">from issue articles</p>
          </div>
        )}
      </div>

      {template?.name && (
        <div 
          className="absolute bottom-2 right-2 text-xs opacity-50"
        >
          Template: {template.name}
        </div>
      )}
    </div>
  );
}

interface LeaderPreviewProps {
  template: MagazineTemplate | null;
  title: MagazineTitle;
  editorName?: string;
  editorMessage?: string;
  className?: string;
}

export function LeaderPreview({
  template,
  title,
  editorName = "The Editor",
  editorMessage,
  className = "",
}: LeaderPreviewProps) {
  const styleConfig = useMemo(() => {
    return (template?.styleConfig as TemplateStyleConfig) || defaultStyleConfig;
  }, [template]);

  return (
    <div 
      className={`relative overflow-hidden p-6 ${className}`}
      style={{ 
        backgroundColor: styleConfig.backgroundColor || "#ffffff",
        fontFamily: styleConfig.primaryFont,
        color: styleConfig.textColor || "#000000",
      }}
      data-testid="leader-preview"
    >
      <h2 
        className="text-2xl font-bold mb-2"
        style={{ fontFamily: styleConfig.headlineFont }}
      >
        From the Editor
      </h2>
      
      <div className="flex gap-6 mt-6">
        <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center shrink-0">
          <span className="text-2xl">ED</span>
        </div>
        <div className="flex-1">
          {editorMessage ? (
            <p className="text-sm leading-relaxed">{editorMessage}</p>
          ) : (
            <div className="space-y-2 text-sm opacity-50">
              <p>Editor's letter content will appear here...</p>
              <p>This section typically includes a welcome message, highlights of key articles, and a personal note from the editor.</p>
            </div>
          )}
          <p className="font-semibold mt-4">{editorName}</p>
        </div>
      </div>

      {template?.name && (
        <div 
          className="absolute bottom-2 right-2 text-xs opacity-50"
        >
          Template: {template.name}
        </div>
      )}
    </div>
  );
}
